<template>
  <div class="collection-condition">
    <a-page-header title="采集工况管理" sub-title="管理故障现象与数据采集工况的对应关系">
      <template #extra>
        <a-space>
          <a-button @click="handleDownloadTemplate">
            <template #icon><DownloadOutlined /></template>
            下载模板
          </a-button>
          <a-upload
            :show-upload-list="false"
            :before-upload="handleImport"
            accept=".xlsx,.xls"
          >
            <a-button :loading="importLoading">
              <template #icon><UploadOutlined /></template>
              批量导入
            </a-button>
          </a-upload>
          <a-button type="primary" @click="showCreateModal">
            <template #icon><PlusOutlined /></template>
            新增采集工况
          </a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <!-- 搜索区域 -->
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item label="关键词">
          <a-input
            v-model:value="searchKeyword"
            placeholder="故障现象/工况名称"
            style="width: 200px"
            allow-clear
            @pressEnter="handleSearch"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="handleSearch">查询</a-button>
          <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>

      <!-- 数据表格 -->
      <a-table
        :columns="columns"
        :data-source="dataList"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'collection_duration'">
            <a-tag color="blue">{{ record.collection_duration }}秒</a-tag>
          </template>
          <template v-else-if="column.key === 'condition_definition'">
            <a-tooltip v-if="record.condition_definition" :title="record.condition_definition">
              <span class="ellipsis-text">{{ record.condition_definition }}</span>
            </a-tooltip>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="showEditModal(record)">编辑</a-button>
              <a-popconfirm
                title="确定要删除此采集工况吗？"
                @confirm="handleDelete(record.id)"
              >
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 新增/编辑弹窗 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? '编辑采集工况' : '新增采集工况'"
      width="600px"
      @ok="handleSubmit"
      :confirmLoading="submitLoading"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-item label="故障现象" name="fault_phenomenon_id">
          <a-select
            v-model:value="formData.fault_phenomenon_id"
            placeholder="请选择故障现象"
            show-search
            :filter-option="filterOption"
            @change="handleFaultPhenomenonChange"
          >
            <a-select-option
              v-for="tag in faultPhenomenonTags"
              :key="tag.id"
              :value="tag.id"
              :label="tag.tag_name"
            >
              {{ tag.tag_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="采集工况名称" name="condition_name">
          <a-input v-model:value="formData.condition_name" placeholder="请输入采集工况名称" />
        </a-form-item>
        <a-form-item label="工况定义" name="condition_definition">
          <a-textarea
            v-model:value="formData.condition_definition"
            placeholder="请输入工况定义描述"
            :rows="4"
          />
        </a-form-item>
        <a-form-item label="采集时间(秒)" name="collection_duration">
          <a-input-number
            v-model:value="formData.collection_duration"
            :min="1"
            :max="300"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="排序号" name="sort_order">
          <a-input-number
            v-model:value="formData.sort_order"
            :min="0"
            style="width: 100%"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { PlusOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons-vue';
import {
  getCollectionConditions,
  createCollectionCondition,
  updateCollectionCondition,
  deleteCollectionCondition,
  getFaultPhenomenonTags,
  downloadTemplate,
  importCollectionConditions
} from '@/api/collectionCondition';

// 数据列表
const dataList = ref([]);
const loading = ref(false);
const searchKeyword = ref('');

// 分页
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`
});

// 表格列定义
const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
  { title: '故障现象', dataIndex: 'fault_phenomenon', key: 'fault_phenomenon', width: 180 },
  { title: '采集工况', dataIndex: 'condition_name', key: 'condition_name', width: 150 },
  { title: '工况定义', dataIndex: 'condition_definition', key: 'condition_definition', ellipsis: true },
  { title: '采集时间', dataIndex: 'collection_duration', key: 'collection_duration', width: 100 },
  { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
  { title: '操作', key: 'action', width: 150, fixed: 'right' }
];

// 弹窗相关
const modalVisible = ref(false);
const isEdit = ref(false);
const submitLoading = ref(false);
const formRef = ref(null);
const editingId = ref(null);

// 故障现象标签列表
const faultPhenomenonTags = ref([]);

// 导入相关
const importLoading = ref(false);

// 表单数据
const formData = reactive({
  fault_phenomenon_id: null,
  fault_phenomenon: '',
  condition_name: '',
  condition_definition: '',
  collection_duration: 30,
  sort_order: 0
});

// 表单验证规则
const formRules = {
  fault_phenomenon_id: [{ required: true, message: '请选择故障现象', trigger: 'change' }],
  condition_name: [{ required: true, message: '请输入采集工况名称', trigger: 'blur' }],
  collection_duration: [{ required: true, message: '请输入采集时间', trigger: 'blur' }]
};

// 获取数据列表
const fetchData = async () => {
  loading.value = true;
  try {
    const res = await getCollectionConditions({
      keyword: searchKeyword.value,
      page: pagination.current,
      pageSize: pagination.pageSize
    });
    if (res.code === 200) {
      dataList.value = res.data.list || [];
      pagination.total = res.data.pagination?.total || 0;
    }
  } catch (error) {
    message.error('获取数据失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

// 获取故障现象标签
const fetchFaultPhenomenonTags = async () => {
  try {
    const res = await getFaultPhenomenonTags();
    if (res.code === 200) {
      faultPhenomenonTags.value = res.data || [];
    }
  } catch (error) {
    console.error('获取故障现象标签失败:', error);
  }
};

// 搜索
const handleSearch = () => {
  pagination.current = 1;
  fetchData();
};

// 重置搜索
const handleReset = () => {
  searchKeyword.value = '';
  pagination.current = 1;
  fetchData();
};

// 表格分页变化
const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchData();
};

// 显示新增弹窗
const showCreateModal = () => {
  isEdit.value = false;
  editingId.value = null;
  Object.assign(formData, {
    fault_phenomenon_id: null,
    fault_phenomenon: '',
    condition_name: '',
    condition_definition: '',
    collection_duration: 30,
    sort_order: 0
  });
  modalVisible.value = true;
};

// 显示编辑弹窗
const showEditModal = (record) => {
  isEdit.value = true;
  editingId.value = record.id;
  Object.assign(formData, {
    fault_phenomenon_id: record.fault_phenomenon_id,
    fault_phenomenon: record.fault_phenomenon,
    condition_name: record.condition_name,
    condition_definition: record.condition_definition,
    collection_duration: record.collection_duration,
    sort_order: record.sort_order || 0
  });
  modalVisible.value = true;
};

// 故障现象选择变化
const handleFaultPhenomenonChange = (value) => {
  const tag = faultPhenomenonTags.value.find(t => t.id === value);
  if (tag) {
    formData.fault_phenomenon = tag.tag_name;
  }
};

// 下拉搜索过滤
const filterOption = (input, option) => {
  return option.label.toLowerCase().includes(input.toLowerCase());
};

// 提交表单
const handleSubmit = async () => {
  try {
    await formRef.value.validate();
    submitLoading.value = true;

    const submitData = {
      fault_phenomenon_id: formData.fault_phenomenon_id,
      fault_phenomenon: formData.fault_phenomenon,
      condition_name: formData.condition_name,
      condition_definition: formData.condition_definition,
      collection_duration: formData.collection_duration,
      sort_order: formData.sort_order
    };

    let res;
    if (isEdit.value) {
      res = await updateCollectionCondition(editingId.value, submitData);
    } else {
      res = await createCollectionCondition(submitData);
    }

    if (res.code === 200) {
      message.success(isEdit.value ? '更新成功' : '创建成功');
      modalVisible.value = false;
      fetchData();
    } else {
      message.error(res.message || '操作失败');
    }
  } catch (error) {
    if (error.errorFields) {
      // 表单验证失败
      return;
    }
    message.error('操作失败：' + error.message);
  } finally {
    submitLoading.value = false;
  }
};

// 删除
const handleDelete = async (id) => {
  try {
    const res = await deleteCollectionCondition(id);
    if (res.code === 200) {
      message.success('删除成功');
      fetchData();
    } else {
      message.error(res.message || '删除失败');
    }
  } catch (error) {
    message.error('删除失败：' + error.message);
  }
};

// 下载模板
const handleDownloadTemplate = async () => {
  try {
    const res = await downloadTemplate();
    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '采集工况导入模板.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
    message.success('模板下载成功');
  } catch (error) {
    message.error('下载模板失败：' + error.message);
  }
};

// 批量导入
const handleImport = async (file) => {
  importLoading.value = true;
  try {
    const res = await importCollectionConditions(file);
    if (res.code === 200) {
      const data = res.data;
      // 显示导入结果
      let content = `导入完成！\n总计: ${data.total} 条\n成功: ${data.success} 条（新增: ${data.created}, 更新: ${data.updated}）\n失败: ${data.failed} 条`;
      
      if (data.errors && data.errors.length > 0) {
        content += '\n\n失败详情：';
        data.errors.slice(0, 10).forEach(err => {
          content += `\n第${err.row}行: ${err.reason}`;
        });
        if (data.errors.length > 10) {
          content += `\n...还有 ${data.errors.length - 10} 条错误`;
        }
      }

      Modal.info({
        title: '导入结果',
        content: content,
        width: 500
      });

      fetchData();
    } else {
      message.error(res.message || '导入失败');
    }
  } catch (error) {
    message.error('导入失败：' + error.message);
  } finally {
    importLoading.value = false;
  }
  return false; // 阻止自动上传
};

// 初始化
onMounted(() => {
  fetchData();
  fetchFaultPhenomenonTags();
});
</script>

<style scoped>
.collection-condition {
  padding: 24px;
}

.ellipsis-text {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

